/**
 * Member Table Row Component
 * 
 * Individual row component for displaying member information in the table.
 */

import * as React from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, MoreVertical, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import type { Member } from "@/services/userService";
import { formatDate } from "../utils/dateUtils";
import { getStatusBadgeClasses, getStatusColor, formatStatusKeanggotaan, getStatusKeanggotaanColor } from "../utils/statusUtils";
import { userService } from "@/services/userService";
import { paymentService } from "@/services/paymentService";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { hasPrivilege } from "@/utils/privileges";

interface MemberTableRowProps {
  member: Member;
  loading: boolean;
  syncingUserId: string | null;
  onSyncStart: (userId: string) => void;
  onSyncEnd: () => void;
  onResetPassword: (userId: string, iar: string, name: string, callsign: string) => void;
  onSetEmail: (userId: string, name: string, callsign: string, currentEmail: string) => void;
  onRefresh: () => void;
}

export function MemberTableRow({
  member,
  loading,
  syncingUserId,
  onSyncStart,
  onSyncEnd,
  onResetPassword,
  onSetEmail,
  onRefresh,
}: MemberTableRowProps) {
  const { user: authUser } = useAuth();
  const canResetPassword = hasPrivilege(authUser, "manage_user_reset_password");
  const canSetEmail = hasPrivilege(authUser, "manage_user_set_email");
  const [location, setLocation] = useLocation();
  const [loadingUserDetail, setLoadingUserDetail] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState<"kta" | "tag" | "callsign" | null>(null);
  const [hasSettlement, setHasSettlement] = React.useState(false);
  const [checkingSettlement, setCheckingSettlement] = React.useState(true);
  const [settlementMasaLakuKta, setSettlementMasaLakuKta] = React.useState<string | null>(null);
  const isIarKehormatan = !member.tipe_iar || member.tipe_iar === "iar_kehormatan";
  const today = Date.now();
  const memberMasaLakuKta = member.masa_laku_kta ? new Date(member.masa_laku_kta).getTime() : 0;
  const settlementMasaLakuKtaMs = settlementMasaLakuKta ? new Date(settlementMasaLakuKta).getTime() : 0;
  const effectiveMasaLakuKta = memberMasaLakuKta || settlementMasaLakuKtaMs;
  const hasValidSdppi = String(member.status_keanggotaan ?? "") === "1" && effectiveMasaLakuKta > today;
  const canDownloadDocs = hasValidSdppi || isIarKehormatan
    || (String(member.status_keanggotaan ?? "") !== "0" && hasSettlement);

  React.useEffect(() => {
    if (hasValidSdppi || isIarKehormatan) {
      setCheckingSettlement(false);
      return;
    }
    if (member.id_client && member.callsign) {
      setCheckingSettlement(true);
      paymentService.checkSettlement(member.id_client, member.callsign)
        .then((result) => {
          setHasSettlement(result.hasSettlement);
          setSettlementMasaLakuKta(result.masa_laku_kta);
        })
        .finally(() => setCheckingSettlement(false));
    }
  }, [member.id_client, member.callsign, hasValidSdppi, isIarKehormatan]);

  const handleSync = async () => {
    onSyncStart(member._id);
    try {
      await userService.syncUserWithSdppi(member._id);
      toast.success(
        `Data ${member.callsign || member.nama_lengkap} berhasil di-sinkronisasi dengan SDPPI`
      );
      onRefresh();
    } catch (err) {
      console.error("Error syncing user:", err);
      toast.error(
        err instanceof Error ? err.message : "Gagal melakukan sinkronisasi"
      );
    } finally {
      onSyncEnd();
    }
  };

  const handleRegenerateKta = async () => {
    setPdfLoading("kta");
    try {
      await userService.downloadKTA(member._id, member.callsign);
      toast.success(`KTA ${member.callsign || ""} diunduh (dibuat ulang dari data terbaru)`);
    } catch (err) {
      console.error("Error regenerating KTA:", err);
      toast.error(err instanceof Error ? err.message : "Gagal membuat ulang KTA");
    } finally {
      setPdfLoading(null);
    }
  };

  const handleRegenerateTag = async () => {
    setPdfLoading("tag");
    try {
      await userService.downloadTag(member._id, member.callsign);
      toast.success(`Tag ${member.callsign || ""} diunduh (dibuat ulang dari data terbaru)`);
    } catch (err) {
      console.error("Error regenerating Tag:", err);
      toast.error(err instanceof Error ? err.message : "Gagal membuat ulang Tag");
    } finally {
      setPdfLoading(null);
    }
  };

  const handleRegenerateCallsign = async () => {
    setPdfLoading("callsign");
    try {
      await userService.downloadCallsign(member._id, member.callsign);
      toast.success(`Papan Callsign ${member.callsign || ""} diunduh (dibuat ulang dari data terbaru)`);
    } catch (err) {
      console.error("Error regenerating Callsign PDF:", err);
      toast.error(err instanceof Error ? err.message : "Gagal membuat ulang Papan Callsign");
    } finally {
      setPdfLoading(null);
    }
  };

  const handleResetPassword = async () => {
    setLoadingUserDetail(true);
    try {
      // Fetch user detail to get IAR value
      const userDetail = await userService.fetchUserDetailById(member._id);
      onResetPassword(
        member._id,
        userDetail.iar || "",
        userDetail.name || member.nama_lengkap,
        userDetail.callsign || member.callsign
      );
    } catch (err) {
      console.error("Error fetching user detail:", err);
      toast.error(err instanceof Error ? err.message : "Gagal memuat data pengguna");
    } finally {
      setLoadingUserDetail(false);
    }
  };

  const handleSetEmail = async () => {
    setLoadingUserDetail(true);
    try {
      const userDetail = await userService.fetchUserDetailById(member._id);
      onSetEmail(
        member._id,
        userDetail.name || member.nama_lengkap,
        userDetail.callsign || member.callsign,
        userDetail.email || member.email || ""
      );
    } catch (err) {
      console.error("Error fetching user detail:", err);
      toast.error(err instanceof Error ? err.message : "Gagal memuat data pengguna");
    } finally {
      setLoadingUserDetail(false);
    }
  };

  // Check if masa_laku contains "2100"
  const hasMasaLaku2100 = member.masa_laku && member.masa_laku.includes("2100");
  
  const rowHighlightClass = cn(hasMasaLaku2100 && "bg-blue-100 dark:bg-blue-900/20");

  const keanggotaanLabel = formatStatusKeanggotaan(member.status_keanggotaan);

  return (
    <TableRow key={member._id} className={rowHighlightClass}>
      <TableCell className="font-medium text-xs">
        <div className="flex flex-col">
          <span>{member.id_client || "-"}</span>
          <span className="text-xs text-muted-foreground">{member.callsign || "-"}</span>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs">
        {member.nama_lengkap || "-"}
      </TableCell>
      <TableCell className="hidden md:table-cell min-w-[150px] max-w-[200px] text-xs">
        <div className="flex flex-col gap-0.5">
          <span className="break-all text-muted-foreground text-[11px]">
            {member.email?.trim() ? member.email : "-"}
          </span>
          <span className="whitespace-nowrap text-muted-foreground text-[11px]">
            {member.no_hp && member.no_hp !== "-" ? member.no_hp : "-"}
          </span>
        </div>
      </TableCell>
      <TableCell
        className="hidden lg:table-cell whitespace-nowrap text-xs text-muted-foreground"
        title={
          !member.lastLoginAt && member.lastSeenAt
            ? "Perkiraan dari unggah / penyimpanan foto KTP (belum ada riwayat login tercatat)"
            : undefined
        }
      >
        {formatDate(member.lastSeenAt ?? member.lastLoginAt ?? null)}
      </TableCell>
      <TableCell className="text-xs">
        <div className="flex flex-col gap-0.5">
          <span className="text-[11px] sm:text-xs">{formatDate(member.masa_laku)}</span>
          <span className="text-xs text-muted-foreground hidden sm:inline">{formatDate(member.masa_laku_kta)}</span>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell text-xs">
        <div className="flex flex-col gap-0.5">
          <span className="text-[11px]">{member.provinsi || "-"}</span>
          <span className="text-xs text-muted-foreground">{member.kabupaten || "-"}</span>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell whitespace-nowrap text-xs">
        <span className={getStatusBadgeClasses(getStatusColor(member.status_callsign))}>
          {member.status_callsign || "-"}
        </span>
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs">
        <span className={getStatusBadgeClasses(getStatusKeanggotaanColor(member.status_keanggotaan))}>
          {keanggotaanLabel}
        </span>
      </TableCell>
      <TableCell className="whitespace-nowrap sticky right-0 bg-background z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="text-xs">
            {canResetPassword && (
              <DropdownMenuItem
                onClick={handleResetPassword}
                disabled={loadingUserDetail}
                className="text-xs"
              >
                Reset Password
              </DropdownMenuItem>
            )}
            {canSetEmail && (
              <DropdownMenuItem
                onClick={handleSetEmail}
                disabled={loadingUserDetail}
                className="text-xs"
              >
                Set Email
              </DropdownMenuItem>
            )}
            {(canResetPassword || canSetEmail) && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={handleSync}
              disabled={syncingUserId === member._id}
              className="text-xs"
            >
              {syncingUserId === member._id ? (
                <>
                  <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  Sinkronisasi...
                </>
              ) : (
                <>
                  <RefreshCw className="h-3 w-3 mr-2" />
                  Re-sinkronisasi
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {canDownloadDocs && (
              <>
                <DropdownMenuItem
                  onClick={handleRegenerateKta}
                  disabled={pdfLoading !== null}
                  className="text-xs"
                >
                  {pdfLoading === "kta" ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-3 w-3 mr-2" />
                  )}
                  Unduh ulang KTA
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleRegenerateTag}
                  disabled={pdfLoading !== null}
                  className="text-xs"
                >
                  {pdfLoading === "tag" ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-3 w-3 mr-2" />
                  )}
                  Unduh ulang Tag
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleRegenerateCallsign}
                  disabled={pdfLoading !== null}
                  className="text-xs"
                >
                  {pdfLoading === "callsign" ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-3 w-3 mr-2" />
                  )}
                  Unduh ulang Papan Callsign
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setLocation(`/user/detail/${member._id}`);
              }}
              className="text-xs"
            >
              Detail User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

